/**
 * Detect common third-party/liveness scripts in baseline HTML and
 * convert them to Policy rule format for preview/prefill.
 */
import {
  PARSER_BASE_HOST,
  normalizeHostname,
  parseUrlWithBase,
  getRootDomain,
} from './url';
const LIVENESS_SCRIPT_CATEGORIES = [
  {
    id: 'googletagmanager',
    label: 'Google Tag Manager',
    type: 'JS',
    field: 'googletagmanager.com',
    patterns: ['googletagmanager\\.com', 'gtm\\.js'],
  },
  {
    id: 'google_analytics',
    label: 'Google Analytics',
    type: 'JS',
    field: 'google-analytics.com',
    patterns: ['google-analytics\\.com', 'gtag\\(', 'analytics\\.js'],
  },
  {
    id: 'nextjs_chunks',
    label: 'Next.js Static Chunks',
    type: 'JS',
    field: '/_next/static/chunks/',
    patterns: ['/_next/static/chunks/', '__NEXT_DATA__'],
  },
  {
    id: 'tiktok_analytics',
    label: 'TikTok Analytics',
    type: 'JS',
    field: 'analytics.tiktok.com',
    patterns: [
      'analytics\\.tiktok\\.com',
      'tiktok\\.com\\/i18n\\/pixel',
      'ttq\\.(load|page|track)',
      'tiktok pixel',
    ],
  },
  {
    id: 'facebook_pixel',
    label: 'Facebook Pixel',
    type: 'JS',
    field: 'connect.facebook.net',
    patterns: ['connect\\.facebook\\.net', 'fbq\\('],
  },
  {
    id: 'hotjar',
    label: 'Hotjar',
    type: 'JS',
    field: 'static.hotjar.com',
    patterns: ['static\\.hotjar\\.com', 'hjSettings'],
  },
  {
    id: 'intercom',
    label: 'Intercom',
    type: 'JS',
    field: 'widget.intercom.io',
    patterns: ['widget\\.intercom\\.io', 'intercomSettings'],
  },
  {
    id: 'segment',
    label: 'Segment',
    type: 'JS',
    field: 'cdn.segment.com',
    patterns: ['cdn\\.segment\\.com', 'analytics\\.identify', 'analytics\\.track'],
  },
  {
    id: 'mixpanel',
    label: 'Mixpanel',
    type: 'JS',
    field: 'cdn.mxpnl.com',
    patterns: ['cdn\\.mxpnl\\.com', 'mixpanel\\.init'],
  },
  {
    id: 'amplitude',
    label: 'Amplitude',
    type: 'JS',
    field: 'cdn.amplitude.com',
    patterns: ['cdn\\.amplitude\\.com', 'amplitude\\.getInstance'],
  },
  {
    id: 'crisp',
    label: 'Crisp Chat',
    type: 'JS',
    field: 'client.crisp.chat',
    patterns: ['client\\.crisp\\.chat', '\\$crisp'],
  },
  {
    id: 'jquery_cdn',
    label: 'jQuery CDN',
    type: 'JS',
    field: 'code.jquery.com',
    patterns: ['code\\.jquery\\.com', 'jquery-[\\d.]+(\\.min)?\\.js'],
  },
  {
    id: 'bootstrap_css_cdn',
    label: 'Bootstrap CDN (CSS)',
    type: 'CSS',
    field: 'cdn.jsdelivr.net/npm/bootstrap',
    patterns: ['cdn\\.jsdelivr\\.net/npm/bootstrap', 'bootstrap(\\.min)?\\.css'],
  },
  {
    id: 'microsoft_clarity',
    label: 'Microsoft Clarity',
    type: 'JS',
    field: 'clarity.ms',
    patterns: ['clarity\\.ms/tag', 'window\\.clarity'],
  },
  {
    id: 'cookieyes',
    label: 'CookieYes',
    type: 'JS',
    field: 'cdn-cookieyes.com',
    patterns: ['cdn-cookieyes\\.com', 'cookieyes'],
  },
  {
    id: 'google_ads',
    label: 'Google Ads / DoubleClick',
    type: 'JS',
    field: 'pagead2.googlesyndication.com',
    patterns: [
      'pagead2\\.googlesyndication\\.com',
      'adsbygoogle',
      'doubleclick\\.net',
      'googletagservices\\.com',
    ],
  },
  {
    id: 'fraudblocker',
    label: 'Fraud Blocker',
    type: 'JS',
    field: 'monitor.fraudblocker.com',
    patterns: ['monitor\\.fraudblocker\\.com', 'fraudblocker\\.com'],
  },
  {
    id: 'clickrank_ai',
    label: 'ClickRank AI',
    type: 'JS',
    field: 'js.clickrank.ai',
    patterns: ['js\\.clickrank\\.ai', 'clickrank\\.ai'],
  },
  {
    id: 'commonninja',
    label: 'CommonNinja',
    type: 'JS',
    field: 'cdn.commoninja.com',
    patterns: ['cdn\\.commonninja\\.com', 'commonninja\\.js', 'commonninja_component'],
  },
  {
    id: 'google_maps_api',
    label: 'Google Maps API',
    type: 'JS',
    field: 'maps.googleapis.com',
    patterns: ['maps\\.googleapis\\.com/maps/api/js', 'maps-api-v3/api/js'],
  },
];

const UNKNOWN_TRACKER_LABEL = 'Potential Tracker';
const VERSIONED_ASSET_LABEL = 'Versioned Third-Party Asset';
const UNKNOWN_TRACKER_THRESHOLD = 2;

const STRONG_TRACKER_TOKENS = new Set([
  'analytics',
  'track',
  'tracker',
  'pixel',
  'beacon',
  'telemetry',
  'metrics',
  'metric',
  'measure',
  'collect',
  'monitor',
  'doubleclick',
  'gtm',
]);

const WEAK_TRACKER_TOKENS = new Set([
  'tag',
  'event',
  'ads',
  'adsense',
  'adservice',
  'session',
  'insight',
  'remarketing',
]);

// Common path markers used by CDNs and vendor bundles before a hashed release/build segment.
// We use these as a confidence boost so the generic detector stays useful without becoming noisy.
const VERSIONED_PATH_MARKERS = new Set([
  'asset',
  'assets',
  'build',
  'builds',
  'bundle',
  'bundles',
  'chunk',
  'chunks',
  'css',
  'js',
  'module',
  'modules',
  'release',
  'releases',
  'script',
  'scripts',
  'static',
  'vendor',
  'vendors',
  'version',
  'versions',
]);

const CATEGORY_MATCHERS = LIVENESS_SCRIPT_CATEGORIES.map(category => ({
  ...category,
  regex: new RegExp(category.patterns.join('|'), 'i'),
}));

// Categories whose field is a relative path (e.g. /_next/static/chunks/) — matched by regex only.
const PATH_ONLY_MATCHERS = CATEGORY_MATCHERS.filter(c => c.field.startsWith('/'));

// Extract the hostname component from a category's field value.
// Returns null for path-only fields (those starting with '/').
// Keep this as a raw-host extractor (not normalizeHostname/extractHostname):
// deriveFieldFromUrl() relies on substring math against the original category.field.
function extractHostnameFromField(field) {
  if (!field || field.startsWith('/')) return null;
  try {
    return new URL('https://' + field).hostname;
  } catch {
    return null;
  }
}

// Treat a URL as third-party only when we can compare it against the page URL.
// This keeps the generic versioned-asset detector from swallowing the site's own hashed bundles.
function isThirdPartyAsset(assetUrl, pageUrl) {
  const parsedAssetUrl = parseUrlWithBase(assetUrl);
  const parsedPageUrl = parseUrlWithBase(pageUrl);

  if (
    !parsedAssetUrl ||
    !parsedAssetUrl.hostname ||
    parsedAssetUrl.hostname === PARSER_BASE_HOST ||
    !parsedPageUrl ||
    !parsedPageUrl.hostname ||
    parsedPageUrl.hostname === PARSER_BASE_HOST
  ) {
    return false;
  }

  const assetHostname = normalizeHostname(parsedAssetUrl.hostname);
  const pageHostname = normalizeHostname(parsedPageUrl.hostname);

  if (assetHostname === pageHostname) {
    return false;
  }

  return getRootDomain(assetHostname) !== getRootDomain(pageHostname);
}

// Build a Map<normalizedHostname, category[]> for O(1) Tier-1 domain lookup.
function buildCategoryHostnameMap() {
  const map = new Map();
  CATEGORY_MATCHERS.forEach(category => {
    const hostname = extractHostnameFromField(category.field);
    if (!hostname) return;
    const norm = normalizeHostname(hostname);
    if (!map.has(norm)) map.set(norm, []);
    map.get(norm).push(category);
  });
  return map;
}

const CATEGORY_HOSTNAME_MAP = buildCategoryHostnameMap();

function extractDomCandidates(doc) {
  const externalAssets = [];

  doc.querySelectorAll('script[src]').forEach(el => {
    const src = el.getAttribute('src');
    if (src) externalAssets.push({ type: 'JS', value: src });
  });

  doc.querySelectorAll('link[href]').forEach(el => {
    const rel = (el.getAttribute('rel') || '').toLowerCase();
    const href = el.getAttribute('href');
    if (href && rel.includes('stylesheet')) {
      externalAssets.push({ type: 'CSS', value: href });
    }
  });

  return { externalAssets };
}

function createPolicyRule({ type, field, label }) {
  return {
    type,
    condition: 'Contains',
    field,
    action: 'Do not monitor',
    label,
  };
}

// Derive the policy field from the actual asset URL and the matched category.
// Preserves the real scheme and hostname (including https://www. when present), then
// appends any path suffix encoded in the category's field definition.
function deriveFieldFromUrl(assetUrl, category) {
  const parsed = parseUrlWithBase(assetUrl);

  if (!parsed || !parsed.hostname || parsed.hostname === PARSER_BASE_HOST) {
    // Relative URL — keep the category's path field as-is.
    return category.field;
  }

  const origin = `${parsed.protocol}//${parsed.hostname}`;

  // Append the path component from the category field if it has one.
  // e.g. 'cdn.jsdelivr.net/npm/bootstrap' → catHostname='cdn.jsdelivr.net', catSuffix='/npm/bootstrap'
  const catHostname = extractHostnameFromField(category.field) || '';
  const catSuffix = catHostname
    ? category.field.slice(category.field.indexOf(catHostname) + catHostname.length)
    : '';

  return `${origin}${catSuffix}`;
}

// Some providers place a generated release/build token in the middle of the path.
// We treat long opaque tokens, semver strings, and large numeric build ids as version segments.
function looksLikeVersionSegment(segment) {
  return (
    /^[a-f0-9]{8,}$/i.test(segment) ||
    /^[A-Za-z0-9_-]{10,}$/.test(segment) ||
    /^v?\d+\.\d+\.\d+(?:[-+][A-Za-z0-9._-]+)?$/i.test(segment) ||
    /^\d{8,}$/.test(segment)
  );
}

// Some CDNs encode the build hash in the filename instead of its own path segment.
// This catches shapes like app.7f3a91c2.js, vendor.abcdef.chunk.js, or 4b8d9e7c.css.
function looksLikeVersionedFilename(fileName) {
  return (
    /^[\w-]+\.[a-f0-9]{6,}\.(?:js|css)$/i.test(fileName) ||
    /^[\w-]+\.[a-f0-9]{6,}\.chunk\.js$/i.test(fileName) ||
    /^[a-f0-9]{6,}\.(?:js|css)$/i.test(fileName)
  );
}

// Query-string cache busters are another common release/version signal.
// We keep the check narrow by requiring a common version key and a value that looks build-like.
function hasVersionLikeQuery(parsedUrl) {
  const versionKeys = new Set(['v', 'ver', 'version', 'build', 'hash', 'rev', 'release']);

  return Array.from(parsedUrl.searchParams.entries()).some(([key, value]) => {
    if (!versionKeys.has(String(key).toLowerCase())) return false;

    return (
      /^[a-f0-9]{6,}$/i.test(value) ||
      /^[A-Za-z0-9_-]{8,}$/.test(value) ||
      /^v?\d+\.\d+\.\d+(?:[-+][A-Za-z0-9._-]+)?$/i.test(value) ||
      /^\d{6,}$/.test(value)
    );
  });
}

// Before we normalize a versioned URL, make sure there is at least some stable structure
// in front of the generated token. This avoids creating rules like https://cdn.example.com/.
function hasLikelyStablePrefix(stableSegments) {
  if (stableSegments.length === 0) return false;

  return (
    stableSegments.some(segment => VERSIONED_PATH_MARKERS.has(String(segment).toLowerCase())) ||
    stableSegments.length >= 2
  );
}

// Collapse a versioned asset URL down to the stable prefix that should be stored in policy rules.
// Examples:
// - https://cdn.example.com/assets/app.7f3a91c2.js -> https://cdn.example.com/assets/
// - https://www.gstatic.com/recaptcha/releases/<token>/recaptcha__fr.js -> https://www.gstatic.com/recaptcha/releases/
function normalizeVersionedAssetField(parsedUrl) {
  const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
  if (pathSegments.length === 0) return null;

  const stableSegments = [];
  let matchedVersionShape = false;

  for (let index = 0; index < pathSegments.length; index += 1) {
    const segment = pathSegments[index];
    const isLastSegment = index === pathSegments.length - 1;

    // Step 1: stop when we hit a standalone version/build segment inside the path.
    if (!isLastSegment && looksLikeVersionSegment(segment)) {
      matchedVersionShape = true;
      break;
    }

    // Step 2: also stop when the final filename itself contains a build hash.
    if (isLastSegment && looksLikeVersionedFilename(segment)) {
      matchedVersionShape = true;
      break;
    }

    // Step 3: keep collecting the stable prefix until we hit one of the version markers above.
    stableSegments.push(segment);
  }

  if (!matchedVersionShape || !hasLikelyStablePrefix(stableSegments)) {
    return null;
  }

  return `${parsedUrl.protocol}//${parsedUrl.hostname}/${stableSegments.join('/')}/`;
}

// Generic fallback for third-party JS/CSS files that use changing release/build identifiers.
// The detector works in four steps:
// 1. Require an absolute third-party URL so first-party hashed bundles are ignored.
// 2. Look for version-like path segments or hashed filenames.
// 3. Fall back to query-string version markers when the pathname itself is stable.
// 4. Normalize the result to a stable prefix before creating a policy rule.
function detectVersionedThirdPartyAsset(asset, pageUrl) {
  if (!asset || !asset.value || !isThirdPartyAsset(asset.value, pageUrl)) {
    return null;
  }

  const parsedAssetUrl = parseUrlWithBase(asset.value);
  if (!parsedAssetUrl || !parsedAssetUrl.hostname || parsedAssetUrl.hostname === PARSER_BASE_HOST) {
    return null;
  }

  const normalizedField = normalizeVersionedAssetField(parsedAssetUrl);

  if (normalizedField) {
    return {
      type: asset.type,
      field: normalizedField,
      label: VERSIONED_ASSET_LABEL,
    };
  }

  // When the build identifier only lives in the query string, keep the stable path and drop the query.
  if (!hasVersionLikeQuery(parsedAssetUrl)) {
    return null;
  }

  if (!/\.(js|css)$/i.test(parsedAssetUrl.pathname)) {
    return null;
  }

  return {
    type: asset.type,
    field: `${parsedAssetUrl.protocol}//${parsedAssetUrl.hostname}${parsedAssetUrl.pathname}`,
    label: VERSIONED_ASSET_LABEL,
  };
}

// Match an asset URL against known categories using the hostname map (Tier 1).
// Returns an array of { category, field } objects where field is derived from the actual URL.
function matchKnownCategories(assetUrl) {
  const results = [];
  const parsed = parseUrlWithBase(assetUrl);

  if (parsed && parsed.hostname && parsed.hostname !== PARSER_BASE_HOST) {
    const hostname = normalizeHostname(parsed.hostname);
    const rootDomain = hostname.split('.').slice(-2).join('.');
    const seen = new Set();

    const addCandidates = candidates => {
      (candidates || []).forEach(category => {
        if (!seen.has(category.id)) {
          seen.add(category.id);
          results.push({ category, field: deriveFieldFromUrl(assetUrl, category) });
        }
      });
    };

    // Try exact normalized hostname first, then fall back to root domain.
    addCandidates(CATEGORY_HOSTNAME_MAP.get(hostname));
    if (rootDomain !== hostname) {
      addCandidates(CATEGORY_HOSTNAME_MAP.get(rootDomain));
    }
  }

  // Path-only patterns (e.g. /_next/static/chunks/) matched by regex.
  PATH_ONLY_MATCHERS.forEach(category => {
    if (category.regex.test(assetUrl)) {
      results.push({ category, field: deriveFieldFromUrl(assetUrl, category) });
    }
  });

  return results;
}

function scorePotentialTracker(assetValue) {
  const parsed = parseUrlWithBase(assetValue);
  const isAbsoluteUrl = parsed && parsed.hostname && parsed.hostname !== PARSER_BASE_HOST;
  const hostname = isAbsoluteUrl ? normalizeHostname(parsed.hostname) : '';
  const path = parsed ? `${parsed.pathname}${parsed.search}` : String(assetValue);

  const tokens = `${hostname} ${path}`
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  let score = 0;
  new Set(tokens).forEach(token => {
    if (STRONG_TRACKER_TOKENS.has(token)) score += 2;
    else if (WEAK_TRACKER_TOKENS.has(token)) score += 1;
  });

  return {
    score,
    field: hostname || String(assetValue).slice(0, 120),
  };
}

function detectWithRegexFallback(html) {
  const matchedRules = [];

  CATEGORY_MATCHERS.forEach(category => {
    if (category.regex.test(html)) {
      matchedRules.push(
        createPolicyRule({
          type: category.type,
          field: category.field,
          label: category.label,
        })
      );
    }
  });

  return matchedRules;
}

function dedupeRawRules(rules) {
  const seenRules = new Map();

  (rules || []).forEach(rule => {
    const key = `${rule.type}__${rule.field}`.toLowerCase();
    if (!seenRules.has(key)) {
      seenRules.set(key, rule);
    }
  });

  return Array.from(seenRules.values());
}

// Validation pass: retain only rules whose field value appears as a substring
// in at least one of the raw asset URLs extracted from the DOM.
// This ensures no rule is suggested for a resource that isn't actually loaded by the page.
// Scheme is stripped before comparison so protocol-relative URLs (//host/path) and
// absolute URLs (https://host/path) are treated equivalently.
function stripScheme(url) {
  return String(url).replace(/^https?:/i, '');
}

function validateRulesAgainstUrls(rules, allUrls) {
  const normalizedUrls = allUrls.map(stripScheme);
  return rules.filter(rule => {
    const normalizedField = stripScheme(rule.field);
    return normalizedUrls.some(url => url.includes(normalizedField));
  });
}

/**
 * Returns policy rules in the same shape expected by PolicyForm.
 *
 * @param {string} html
 * @param {string} [pageUrl]
 * @returns {Array<{type: string, condition: string, field: string, action: string, severity: number, label: string}>}
 */
export function detectLivenessScripts(html, pageUrl = '') {
  if (!html) return [];

  if (typeof DOMParser === 'undefined') {
    return dedupeRawRules(detectWithRegexFallback(html)).map((rule, index) => ({
      ...rule,
      severity: (index + 1) * 10,
    }));
  }

  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  const { externalAssets } = extractDomCandidates(parsedDocument);

  // Collect all raw URL strings for the validation pass.
  const allUrls = externalAssets.map(a => a.value);
  const matchedRules = [];

  externalAssets.forEach(asset => {
    // Tier 1: domain-based lookup — field is derived from the actual URL's scheme + hostname.
    const knownMatches = matchKnownCategories(asset.value);
    if (knownMatches.length > 0) {
      knownMatches.forEach(({ category, field }) => {
        matchedRules.push(createPolicyRule({ type: category.type, field, label: category.label }));
      });
      return;
    }

    // Tier 2: generic version-aware matcher for third-party JS/CSS assets that ship with
    // changing release tokens, hashed path segments, or cache-busting version params.
    const versionedAssetRule = detectVersionedThirdPartyAsset(asset, pageUrl);
    if (versionedAssetRule) {
      matchedRules.push(createPolicyRule(versionedAssetRule));
      return;
    }

    // Tier 3: heuristic token scorer for unrecognised JS resources.
    if (asset.type !== 'JS') return;
    const { score, field } = scorePotentialTracker(asset.value);
    if (score >= UNKNOWN_TRACKER_THRESHOLD) {
      matchedRules.push(createPolicyRule({ type: 'JS', field, label: UNKNOWN_TRACKER_LABEL }));
    }
  });

  // Validation pass: only keep rules whose field actually appears in at least one extracted URL.
  // This prevents false positives where a hostname matched a category but the specific
  // resource path (e.g. /npm/bootstrap) was never loaded.
  const validated = validateRulesAgainstUrls(matchedRules, allUrls);
  const deduplicated = dedupeRawRules(validated);

  return deduplicated.map((rule, index) => ({
    ...rule,
    severity: (index + 1) * 10,
  }));
}

